import React, { useCallback, useRef, useState } from 'react';
import DropdownListContainer from '~/components/molecule/Dropdown/components/DropdownListContainer';
import SearchInput from '~/components/molecule/SearchInput';
import { Marker, useMap } from 'react-map-gl';
import useErrorReporter from '~/hooks/useErrorReporter';
import { isNil } from 'ramda';
import Catalog from '~/Catalog';
import type { OptionOf } from '~/components/molecule/Dropdown';
import styled, { css } from 'styled-components';
import { LeftAsideContainer } from '../../style';
import Icon from '~/components/atom/Icon';
import type { GeocodingFeature } from './types';
import isAddressFeature from './utils/isAddressFeature';

const text = {
  placeholder: 'Locatie zoeken',
};

export type Props = {
  mapboxAccessToken: string;
};

const NO_RESULT_OPTION = {
  key: 'no-results',
  label: Catalog.noResults,
  payload: null,
  type: 'DISABLED' as const,
};

const GEOCODING_V6_FORWARD_URL =
  'https://api.mapbox.com/search/geocode/v6/forward';

// Get results closest to the user's IP location
const PROXIMITY = 'ip';
const LANGUAGE = 'NL';
const LIMIT = 7;

const GeocodeSearchInput: React.FCC<Props> = ({ mapboxAccessToken }) => {
  const errorReporter = useErrorReporter();

  const inputContainerRef = useRef<HTMLDivElement | null>(null);

  const [options, setOptions] = useState<
    Array<OptionOf<GeocodingFeature | null>>
  >([]);
  const [loading, setLoading] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<
    [number, number] | null
  >(null);

  const { current: map } = useMap();

  const onSearch = useCallback(
    async (query: string) => {
      setSelectedLocation(null);

      if (query.length === 0) {
        return setOptions([]);
      }

      setLoading(true);

      const url = `${GEOCODING_V6_FORWARD_URL}?q=${query}&access_token=${mapboxAccessToken}&language=${LANGUAGE}&proximity=${PROXIMITY}&limit=${LIMIT}`;

      try {
        const res = await fetch(url);

        if (!res.ok) {
          throw new Error(`Geocoding API error: ${res.statusText}`);
        }

        const data = await res.json();
        const features = data.features as Array<GeocodingFeature>;

        if (features.length > 0) {
          setOptions(
            features.map(feature => ({
              key: feature.properties.mapbox_id,
              payload: feature,
              label:
                feature.properties.name +
                (feature.properties.place_formatted
                  ? ` - ${feature.properties.place_formatted}`
                  : ''),
            })),
          );
        } else {
          setOptions([NO_RESULT_OPTION]);
        }
      } catch (error) {
        setOptions([]);
        errorReporter.captureException(error);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mapboxAccessToken],
  );

  const onSelectOption = (selectedFeature: GeocodingFeature) => {
    setOptions([]);

    if (selectedFeature) {
      const location =
        selectedFeature.geometry?.type === 'Point'
          ? selectedFeature.geometry.coordinates
          : null;

      if (!isNil(location)) {
        setSelectedLocation(
          isAddressFeature(selectedFeature) ? location : null,
        );

        if (map) {
          map.flyTo({
            center: location,
            zoom: 11,
            speed: 1.5,
            // Center it between Aside and left side
            offset: [-150, 0],
          });
        }
      }
    }
  };

  return (
    <>
      <Container $highlighted={!isNil(selectedLocation)}>
        <div ref={inputContainerRef}>
          <SearchInput
            onInputChange={onSearch}
            size="small"
            placeholder={text.placeholder}
          />
        </div>
        <DropdownListContainer
          options={options}
          dropdownListOpen={options?.length > 0}
          onChange={({ selectedOptionIdx }) => {
            const selectedOption = options[selectedOptionIdx];
            if (selectedOption && selectedOption.payload !== null)
              onSelectOption(selectedOption.payload);
          }}
          onClickOutside={() => setOptions([])}
          withDefaultFirstOption
          loading={loading}
          openerRef={inputContainerRef}
        />
      </Container>
      {selectedLocation && (
        <Marker
          longitude={selectedLocation[0]}
          latitude={selectedLocation[1]}
          anchor="bottom"
          style={{ pointerEvents: 'none' }}
        >
          <Icon
            name="map-pin"
            color={{ group: 'accent', variant: 'base' }}
            size="xxl"
          />
        </Marker>
      )}
    </>
  );
};

const Container = styled(LeftAsideContainer)<{ $highlighted: boolean }>(
  ({ theme, $highlighted }) => css`
    width: 250px;
    bottom: calc(${theme.space('xxxxl')} + ${theme.space('base')});

    /** Give it the same border-radius as the input */
    border-radius: ${theme.getTokens().border.radius.base};

    ${$highlighted &&
    css`
      /* Keep the inner input border in the same accent as the map pin */
      * {
        border-color: ${theme.color('accent')};
      }
    `}
  `,
);

export default GeocodeSearchInput;
